import { TableProps, Card, Flex, Table } from "antd";
import { colors } from "shared/theme/color";
import CardSectionTitle from "components/CardSectionTitle.component";
import { getAllCustomerPackagesByCustomerId } from "hooks/queries/customerpackage";
import { useNavigate } from "react-router-dom";
import {
  StatusBadge,
  StatusTone,
} from "pages/schedule/component/ScheduleStatusBadge";
import { th, tnum } from "shared/components/TableText";
import { PAGE_SIZE } from "shared/constants";
import PATHS from "shared/constants/paths";
import { translate, translateWithVars } from "shared/languages";
import { CustomerPackage, InactiveReason, User } from "shared/types";
import {
  INACTIVE_REASON_LABELS,
  INACTIVE_REASON_TONE,
  UPGRADE_LABEL,
  VOIDED_TEXT,
  amountPaid,
  formatCurrency,
  productTypeLabel,
  rewardSourceOf,
} from "shared/utils";
import { DEFAULT_DATE_FORMAT, formatDate, tzDate } from "shared/utils/dayjs";
import { formatTransactionNumber } from "shared/utils/transactionNumber";
import { discountSummary } from "shared/utils/purchaseDiscount";
import { textPx } from "shared/constants/zoom";

// Money/paperwork view of a customer's purchases — merges Paket AND
// Membership into one chronological list (unlike CustomerPackage.tsx, which
// stays split by type and is purely credit-inventory focused now that
// Metode Pembayaran lives here instead). No active/expired split: a
// purchase's validity as remaining credit doesn't matter for a payment
// record, matching the old platform's flat Transactions list. Invoice/Receipt
// downloads live on the package detail page (row click) rather than as
// icon-only buttons here — those gave no visual cue which icon was which.
const typeTone = (isMembership: boolean): StatusTone =>
  isMembership ? "navy" : "neutral";

const CustomerTransactionsPage = ({ customer }: { customer: User }) => {
  const navigate = useNavigate();
  const { data: customerPackages, isLoading } =
    getAllCustomerPackagesByCustomerId(customer.id);
  // A claimed reward credit is a gift, not a sale: no payment, no transaction
  // number. It stays on the Paket tab (what the client holds) and leaves this
  // one (what the client paid for).
  const transactions = [
    ...(customerPackages?.active ?? []),
    ...(customerPackages?.expired ?? []),
  ].filter((cp) => rewardSourceOf(cp.package_deal) === null);

  const columns: TableProps<CustomerPackage>["columns"] = [
    {
      title: th(translate("analyticsPage.sales.col_purchased_at")),
      dataIndex: "purchase_date",
      key: "purchase_date",
      sorter: (a, b) => tzDate(a.purchase_date).diff(b.purchase_date),
      defaultSortOrder: "descend",
      render: (_, { purchase_date }) => (
        <span style={tnum}>
          {formatDate(purchase_date, DEFAULT_DATE_FORMAT + ", HH:mm")}
        </span>
      ),
    },
    {
      title: th(translate("label.transaction_id")),
      key: "transaction_number",
      render: (_, { transaction_number }) => (
        <span style={tnum}>{formatTransactionNumber("TRX", transaction_number)}</span>
      ),
    },
    {
      title: th(translate("label.package_name")),
      dataIndex: "name",
      // Two qualifiers, because a bare package name misdescribes two kinds of
      // row on a tab that reads as "what this client paid":
      //   (Upgrade)      — the amount beside it is a difference, not the
      //                    package's price, so without this the row looks like
      //                    an unexplained discount. Same label the Pembelian
      //                    report uses.
      //   Dibagikan oleh — a package this client was shared INTO. They did not
      //                    pay for it; the owner did. Labelled rather than
      //                    hidden, so nothing an admin sees today disappears.
      render: (_, { package_deal, upgrade_fee, is_shared_with_me, owner_name, voided_at }) => (
        <Flex vertical gap={2}>
          <Flex align="center" gap={6} wrap="wrap">
            <span
              style={{
                fontWeight: 600,
                // A voided sale is kept on the list and struck through: it
                // happened, it is simply not a sale any more.
                ...(voided_at ? VOIDED_TEXT : {}),
              }}
            >
              {package_deal.name}
            </span>
            {/* The two chips the name can carry, both fixed English like every
                other chip: "Upgrade" is a LABEL (what this row is, no dot),
                "Voided" is a STATUS (what happened to it, with its dot). */}
            {upgrade_fee != null && (
              <StatusBadge tone="navy" label={UPGRADE_LABEL} dot={false} />
            )}
            {!!voided_at && (
              <StatusBadge
                tone={INACTIVE_REASON_TONE[InactiveReason.MANUALLY_MARKED_INVALID]}
                label={INACTIVE_REASON_LABELS[InactiveReason.MANUALLY_MARKED_INVALID]}
              />
            )}
          </Flex>
          {is_shared_with_me && !!owner_name && (
            <span style={{ fontSize: textPx(12), color: colors.ink2 }}>
              {translateWithVars("label.shared_by_owner", { owner: owner_name })}
            </span>
          )}
        </Flex>
      ),
    },
    {
      title: th(translate("label.type")),
      key: "type",
      render: (_, { package_deal }) => (
        <StatusBadge
          tone={typeTone(package_deal.unlimited)}
          label={productTypeLabel(package_deal.unlimited)}
          dot={false}
        />
      ),
    },
    {
      // The invoice's and Pembelian's word for the same figure, so one sale
      // reads the same on every surface. The tooltip names the basis, since
      // this column is the money received (tax included) while Pembelian's
      // Harga Jual beside it is before tax.
      title: th(
        translate("analyticsPage.columns.total_paid"),
        translate("analyticsPage.columns.total_paid_info"),
      ),
      key: "purchase_price",
      // amountPaid, not purchase_price: an upgrade row carries the new
      // package's full catalogue price but only its fee was collected. This tab
      // is the money/paperwork view, so it has to agree with the Invoice and
      // the Pembelian report rather than with the credit maths.
      // The discount, if any, sits under the price as a note rather than in a
      // column of its own: most purchases have none, and the price stays the
      // amount paid either way.
      render: (_, record) => {
        const discount = discountSummary(record);
        return (
          <Flex vertical gap={2}>
            <span style={{ ...tnum, ...(record.voided_at ? VOIDED_TEXT : {}) }}>
              {formatCurrency(amountPaid(record))}
            </span>
            {discount && (
              <span style={{ ...tnum, fontSize: textPx(12), color: colors.ink2 }}>
                {discount}
              </span>
            )}
          </Flex>
        );
      },
      sorter: (a, b) => amountPaid(a) - amountPaid(b),
    },
    {
      title: th(translate("paymentMethods.col_payment_method")),
      dataIndex: "payment_method_name",
      key: "payment_method_name",
      render: (value: string | null) => value ?? "-",
    },
  ];

  return (
    <Card>
      <Flex vertical gap={18}>
        <CardSectionTitle title={translate("tabs.transactions")} />
        <Table
          showSorterTooltip={false}
          rowKey="id"
          size="middle"
          dataSource={transactions}
          columns={columns}
          loading={isLoading}
          pagination={{
            pageSize: PAGE_SIZE,
            showSizeChanger: false,
          }}
          onRow={(record) => ({
            onClick: () => navigate(`${PATHS.CUSTOMER_PACKAGE}/${record.id}`),
          })}
        />
      </Flex>
    </Card>
  );
};

export default CustomerTransactionsPage;
